import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push, goBack } from 'react-router-redux';
import { firebaseConnect, getFirebase, isLoaded } from 'react-redux-firebase';
import moment from 'moment';

import './JobForm.css';
import logoUrl from 'assets/img/logo-black.png';

import { toast } from 'react-toastify';
import { Job } from '../../../utils/variables';
import LoadingSpinner from 'components/global/LoadingSpinner/LoadingSpinner';
import FormField from '../../contentform/FormField/FormField';

import truncateString from 'utils/truncateString';

class JobForm extends React.Component {
  static propTypes = {
    jobKey: PropTypes.string,
    jobsIndex: PropTypes.object,
    companies: PropTypes.object,
    location: PropTypes.object
  };

  state = {
    departmentIndex: null,
    countryIndex: null,
    jobInfo: null,
    _scrolledSection: null
  };

  databaseToast = null;

  // First, retrieve the company key from the URL
  // Load company and all related parts once from Firebase
  componentDidMount() {
    const { jobKey } = this.props;
    const firebase = getFirebase();

    if (jobKey && jobKey !== 'new') {
      const tagJobDepartmentIndexRef = firebase.database().ref(`/tagJobDepartmentIndex`);
      const tagJobCountryIndexRef = firebase.database().ref(`/tagJobCountryIndex`);
      const jobInfoRef = firebase.database().ref(`/job/${jobKey}`);

      const retrievePromises = [
        tagJobDepartmentIndexRef.once('value'),
        tagJobCountryIndexRef.once('value'),
        jobInfoRef.once('value')
      ];

      Promise.all(retrievePromises)
        .then((results) => {
          const data = results.map(result => result.val());

          const tagJobDepartmentIndex = data[0] ? Object.keys(data[0]).map(key => ({ value: key, label: key })) : [];
          const tagJobCountryIndex = data[1] ? Object.keys(data[1]).map(key => ({ value: key, label: key })) : [];
          const jobInfoMerged = data[2] ? Object.assign(new Job(), data[2]) : new Job();
          this.setState({
            departmentIndex: tagJobDepartmentIndex,
            countryIndex: tagJobCountryIndex,
            jobInfo: jobInfoMerged,
            _dbLoaded: true
          });
        });
    } else if (jobKey === 'new') {
      const tagJobDepartmentIndexRef = firebase.database().ref(`/tagJobDepartmentIndex`);
      const tagJobCountryIndexRef = firebase.database().ref(`/tagJobCountryIndex`);

      const retrievePromises = [
        tagJobDepartmentIndexRef.once('value'),
        tagJobCountryIndexRef.once('value')
      ];

      Promise.all(retrievePromises)
        .then((results) => {
          const data = results.map(result => result.val());

          const tagJobDepartmentIndex = data[0] ? Object.keys(data[0]).map(key => ({ value: key, label: key })) : [];
          const tagJobCountryIndex = data[1] ? Object.keys(data[1]).map(key => ({ value: key, label: key })) : [];
          this.setState({
            departmentIndex: tagJobDepartmentIndex,
            countryIndex: tagJobCountryIndex,
            jobInfo: new Job(),
            _dbLoaded: true
          });
        });
    }
  }

  handleJobStatusChange = (option) => {
    let newState = Object.assign(this.state);
    newState.jobInfo.status = option.value;
    this.setState(newState);
  }

  // Change company info, with optional limiter of string length
  handleJobInfoChange = (e, lengthLimit) => {
    let newState = Object.assign(this.state);
    const value = lengthLimit ? truncateString(e.target.value, lengthLimit) : e.target.value;
    newState.jobInfo[e.target.name] = value;
    this.setState(newState);
  }

  handleJobCompanyChange = (option) => {
    let newState = Object.assign(this.state);
    newState.jobInfo.company = option.value;
    this.setState(newState);
  }

  handleJobTypeChange = (option) => {
    let newState = Object.assign(this.state);
    newState.jobInfo.type = option.value;
    this.setState(newState);
  }

  handleJobDepartmentChange = (option) => {
    let newState = Object.assign(this.state);
    newState.jobInfo.department = option.value;
    this.setState(newState);
  }

  handleJobCountryChange = (option) => {
    let newState = Object.assign(this.state);
    newState.jobInfo.country = option.value;
    this.setState(newState);
  }

  handleJobExperienceChange = (option) => {
    let newState = Object.assign(this.state);
    newState.jobInfo.experienceRequired = option.value;
    this.setState(newState);
  }

  handleJobDatesChange = (dates) => {
    let newState = Object.assign(this.state);
    newState.jobInfo.startDate = dates.startDate ? dates.startDate.toISOString() : null;
    newState.jobInfo.endDate = dates.endDate ? dates.endDate.toISOString() : null;
    this.setState(newState);
  }

  handleSave = () => {
    const companyKey = this.state.jobInfo.company;
    const jobKey = this.state.jobInfo.code;
    const now = moment().toISOString();
    const datePublished = (!this.state.jobInfo.datePublished && this.state.jobInfo.status === 'published') ? now : '';

    // Set updated and published dates if applicable
    this.setState({
      ...this.state,
      jobInfo: {
        ...this.state.jobInfo,
        datePublished: this.state.jobInfo.datePublished ? this.state.jobInfo.datePublished : datePublished,
        dateUpdated: now,
        codeLocked: true
      }
    }, () => {
      this.databaseToast = toast.info('Saving...');
      if (jobKey) {
        this.setState({ _isBusy: true });

        const infoRef = `/job/${companyKey}:${jobKey}`;
        const indexRef = `/jobIndex/${companyKey}:${jobKey}`;
        const tagDepartmentIndexRef = `/tagJobDepartmentIndex/${this.state.jobInfo.department}`;
        const tagCountryIndexRef = `/tagJobCountryIndex/${this.state.jobInfo.country}`;

        let updatePromises = [
          this.props.firebase.update(infoRef, this.state.jobInfo),
          this.props.firebase.update(indexRef, {
            status: this.state.jobInfo.status,
            code: this.state.jobInfo.code,
            company: this.state.jobInfo.company,
            datePublished: this.state.jobInfo.datePublished ? this.state.jobInfo.datePublished : datePublished,
            title: this.state.jobInfo.title,
            type: this.state.jobInfo.type,
            department: this.state.jobInfo.department,
            country: this.state.jobInfo.country
          }),
          this.props.firebase.set(tagDepartmentIndexRef, true),
          this.props.firebase.set(tagCountryIndexRef, true)
        ];

        Promise.all(updatePromises)
          .then(() => {
            this.setState({ _isBusy: false });
            toast.update(
              this.databaseToast, {
                render: 'Save success!',
                type: toast.TYPE.SUCCESS,
                autoClose: 3000,
                className: 'JobForm-toast-transition'
              }
            );
            if (this.props.jobKey === 'new') {
              this.props.dispatch(push(`/jobform?company=${this.state.jobInfo.company}&job=${this.state.jobInfo.code}`));
            }
          })
          .catch((error) => {
            this.setState({ _isBusy: false });
            toast.update(
              this.databaseToast, {
                render: `Error: ${error.message}`,
                type: toast.TYPE.ERROR,
                autoClose: 3000,
                className: 'JobForm-toast-transition'
              }
            );
          });
      }
    });
  }

  handleNavBack = () => {
    this.props.dispatch(goBack());
  }

  _formComplete = () => {
    const { jobInfo } = this.state;
    if (!jobInfo.code || !jobInfo.title || !jobInfo.company || !jobInfo.applicationUrl || !jobInfo.type || !jobInfo.department || !jobInfo.country) {
      return false;
    }
    return true;
  }

  _renderCompanyOptions = () => {
    const { companies } = this.props;
    return (
      Object.keys(companies).map(companyCode => (
        { value: companyCode, label: companies[companyCode].name }
      ))
    );
  }

  render() {
    const { jobKey, jobsIndex, companies } = this.props;
    const { departmentIndex, countryIndex, jobInfo } = this.state;

    return (
      <div className='JobForm-root'>
        {
          !this.state._dbLoaded || !isLoaded(jobsIndex) || !isLoaded(companies) ?
          <div className='JobForm-loadingContainer'>
            <img className='JobForm-headerImage' src={logoUrl} alt='' />
            <LoadingSpinner />
          </div> :
          <div className='JobForm-page'>
            <div className='JobForm-header'>
              <div className='JobForm-title'>
                <button onClick={this.handleNavBack}>
                  <i className='fa fa-chevron-left' />
                </button>
                <h2>{jobInfo.company ? companies[jobInfo.company].name : 'Unknown Company'} - {jobInfo.title || 'New Job'}</h2>
              </div>
              <button
                disabled={!this.state._dbLoaded || !this._formComplete()}
                className='JobForm-submitButton'
                type='button'
                onClick={this.handleSave}
              >
                SAVE {(!this.state._dbLoaded || !this._formComplete()) && '(disabled)'}
              </button>
            </div>
            <div className='JobForm-formContainer'>
              <form className='JobForm-form'>
                <h2 className='CompanyForm-sectionHeader' ref='publish'>
                  PUBLISH STATUS
                  <p className='CompanyForm-sectionSubheader'>Toggle the button below to publish the entry. DO NOT PUBLISH IF FIELDS HAVE NOT YET BEEN FILLED.</p>
                  <p className='CompanyForm-sectionSubheader'>NOTE: The date and time will be saved and tagged the first time you publish.</p>
                </h2>
                <FormField
                  type='select'
                  name='status'
                  title='Publish Status'
                  subtitle='Set to published in order to show the entry publicly'
                  value={jobInfo.status}
                  options={[
                    { value: 'draft', label: 'Draft' },
                    { value: 'published', label: 'Published' },
                    { value: 'disabled', label: 'Disabled' }
                  ]}
                  onChange={this.handleJobStatusChange}
                />
                <h2 className='JobForm-sectionHeader' ref='essential'>
                  Essential Info
                  <p className='JobForm-sectionSubheader'>Fill all fields in this section to enable saving</p>
                </h2>
                <FormField
                  type='select'
                  name='company'
                  title='Company*'
                  subtitle='The company that this job belongs to'
                  value={jobInfo.company}
                  disabled={jobInfo.code === jobKey}
                  options={this._renderCompanyOptions()}
                  onChange={this.handleJobCompanyChange}
                />
                <FormField
                  type='text'
                  name='code'
                  title='Job Code*'
                  subtitle={
                    `A UNIQUE-TO-COMPANY one-word, all-smallcaps slug that will show up on the URL (e.g. https://talenttribe.asia/{company}/jobs/{code})
                    The slug can have dashes (e.g. "assistant-to-the-regional-manager")
                    NOTE: ONCE SAVED, CANNOT BE CHANGED`
                  }
                  value={jobInfo.code}
                  disabled={jobInfo.codeLocked || !jobInfo.company}
                  errorCode={(jobKey === 'new' && jobInfo.company && Object.keys(jobsIndex).indexOf(`${jobInfo.company}:${jobInfo.code}`) !== -1) ? 'duplicate-code' : null}
                  onChange={this.handleJobInfoChange}
                />
                <FormField
                  type='text'
                  name='title'
                  title='Job Title*'
                  subtitle='Self-explanatory'
                  value={jobInfo.title}
                  onChange={this.handleJobInfoChange}
                />
                <FormField
                  type='text'
                  name='applicationUrl'
                  title='Application URL*'
                  subtitle='Link to the company application page for this specific job'
                  value={jobInfo.applicationUrl}
                  onChange={this.handleJobInfoChange}
                />
                <FormField
                  type='select'
                  name='type'
                  title='Job Type*'
                  subtitle='Tag for type so that users can filter while browsing'
                  value={jobInfo.type}
                  options={[
                    { value: 'fulltime', label: 'Full-Time' },
                    { value: 'parttime', label: 'Part-Time' },
                    { value: 'contract', label: 'Contract' },
                    { value: 'internship', label: 'Internship' }
                  ]}
                  onChange={this.handleJobTypeChange}
                />
                <FormField
                  type='tag'
                  name='department'
                  title='Job Department*'
                  subtitle='Tag for department so that users can filter while browsing (caps-sensitive)'
                  value={jobInfo.department}
                  options={departmentIndex}
                  onChange={this.handleJobDepartmentChange}
                />
                <FormField
                  type='tag'
                  name='country'
                  title='Job Country*'
                  subtitle='Tag for country so that users can filter while browsing (caps-sensitive)'
                  value={jobInfo.country}
                  options={countryIndex}
                  onChange={this.handleJobCountryChange}
                />
                <FormField
                  type='select'
                  name='experienceRequired'
                  title='Job Experience Required*'
                  subtitle='Tag for experience required (yes or no only) so that users can filter while browsing (caps-sensitive)'
                  value={jobInfo.experienceRequired}
                  options={[
                    { value: false, label: 'No' },
                    { value: true, label: 'Yes' },
                  ]}
                  onChange={this.handleJobExperienceChange}
                />
                <br />
                <h2 className='JobForm-sectionHeader' ref='address'>Job Page Info</h2>
                <FormField
                  type='text'
                  name='targetExperience'
                  title='Target Experience'
                  subtitle='Targetted job experience for the role (e.g. "Fresh Graduates")'
                  value={jobInfo.targetExperience}
                  onChange={this.handleJobInfoChange}
                />
                <FormField
                  type='text'
                  name='targetNationality'
                  title='Target Nationality'
                  subtitle='Targetted natonality for the role'
                  value={jobInfo.targetNationality}
                  onChange={this.handleJobInfoChange}
                />
                <FormField
                  type='text'
                  name='targetPreference'
                  title='Any Other Preference'
                  subtitle='State any other preferences (e.g. Degree type)'
                  value={jobInfo.targetPreference}
                  onChange={this.handleJobInfoChange}
                />
                <FormField
                  type='daterange'
                  name='dates'
                  title='Application Window*'
                  subtitle='Date range for job applications (leave blank to indicate a perpetually-available job)'
                  value={{ startDate: jobInfo.startDate ? moment(jobInfo.startDate) : null, endDate: jobInfo.endDate ? moment(jobInfo.endDate) : null }}
                  onChange={this.handleJobDatesChange}
                />
                <FormField
                  type='textarea'
                  name='overseasPoints'
                  title='Overseas Opportunities'
                  subtitle='List of potential overseas opportunities - each new line is a bullet point'
                  value={jobInfo.overseasPoints}
                  onChange={this.handleJobInfoChange}
                />
                <h2 className='JobForm-sectionHeader' ref='content'>Job Page Content</h2>
                <FormField
                  type='textarea'
                  name='aboutPara'
                  title='&quot;About the Company&quot; Paragraph'
                  subtitle='Short paragraph about the company owning this job listing'
                  value={jobInfo.aboutPara}
                  onChange={this.handleJobInfoChange}
                />
                <FormField
                  type='textarea'
                  name='rolePara'
                  title={'"Here\'s what you\'ll do" Paragraph (optional)'}
                  subtitle='Text on job role and responsibilities - each new line is a bullet point'
                  value={jobInfo.rolePara}
                  onChange={this.handleJobInfoChange}
                />
                <FormField
                  type='textarea'
                  name='rolePoints'
                  title={'"Here\'s what you\'ll do" List'}
                  subtitle='List of points on job role and responsibilities - each new line is a bullet point'
                  value={jobInfo.rolePoints}
                  onChange={this.handleJobInfoChange}
                />
                <FormField
                  type='textarea'
                  name='reqPara'
                  title='&quot;Skills and Requirements&quot; Paragraph (optional)'
                  subtitle='Text on job skills and requirements - each new line is a bullet point'
                  value={jobInfo.reqPara}
                  onChange={this.handleJobInfoChange}
                />
                <FormField
                  type='textarea'
                  name='reqPoints'
                  title='&quot;Skills and Requirements&quot; List'
                  subtitle='List of points on job skills and requirements - each new line is a bullet point'
                  value={jobInfo.reqPoints}
                  onChange={this.handleJobInfoChange}
                />
                <br />
              </form>
            </div>
          </div>
        }
      </div>
    );
  }
}

const connectedJobForm = connect(
  (state) => ({
    jobsIndex: state.firebase.data.jobIndex,
    companies: state.firebase.data.company,
    location: state.router.location
  })
)(JobForm);

const wrappedJobForm = firebaseConnect(
  props => ([
    { path: `/jobIndex` },
    { path: '/company' }
  ])
)(connectedJobForm);

export default wrappedJobForm;



// WEBPACK FOOTER //
// ./src/components/jobform/JobForm/JobForm.js